import {AppBar, Button, Tab, Tabs, MenuItem} from '@material-ui/core';
import withWidth, {WithWidth} from '@material-ui/core/withWidth';
import * as ModalActions from 'Core/ducks/modal';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {AsyncPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {withSelectFilterProperties, withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {Scope} from 'Shared/constants/Scope';
import {hasAccessToScopeByApplication} from 'Shared/utilities/applicationId';
import {withValueChanged} from 'Shared/utilities/withValueChanged';
import AddUsersMenu from 'Users/components/UserAdministration/AddUsersMenu';
import DeleteUserConfirmationModal from 'Users/components/UserAdministration/DeleteUserConfirmationModal';
import * as UserActions from 'Users/ducks/userAdministration';
import {UserProfileActions} from 'Users/ducks/userProfile';
import {Application} from 'Users/models/Application';
import {AsyncUserClaims} from 'Users/models/Claims';
import {LineOfBusiness, LinesOfBusiness} from 'Users/models/LineOfBusiness';
import {UserRouteConstants} from 'Users/Routes';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import PendingUserRegistration from './PendingUserRegistration';
import './UserAdministration.sass';
import ResetUserPasswordConfirmationModal from 'Users/components/UserAdministration/ResetUserPasswordConfirmationModal';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';

export type UserViewProps = {
  scopes: Scope[];
  userClaims: AsyncUserClaims;
  users: AsyncPage<Page<UserActions.UserType>>;
  fetchUsers: typeof UserActions.fetchUsers;
  changeUserFilter: typeof UserActions.changeUserFilter;
  filter: UserActions.UserFilter;
  loadUser: typeof UserActions.loadUser;
} & typeof ModalActions &
  typeof UserProfileActions &
  WithWidth &
  RouteComponentProps;

type UserConfirmationState = {userId: string; username: string};
type DeleteConfirmationState = {user: UserActions.UserType};

export const UsersView = (props: UserViewProps) => {
  const {filter, match, changeUserFilter, fetchUsers, users, scopes} = props;

  useEffect(() => {
    fetchUsers(undefined, filter);
  }, [fetchUsers, filter]);

  useBreadcrumbs('User Administration', match.url);

  const [deleteConfirmation, setDeleteConfirmation] = useState<O.Option<DeleteConfirmationState>>(O.none);
  const [userToSendTemporaryPassword, setUserToSendTemporaryPassword] = useState<UserConfirmationState | null>(null);
  const changeFilter = withValueChanged(filter, changeUserFilter);
  const hideDeleteConfirmation = () => {
    setDeleteConfirmation(O.none);
    fetchUsers(undefined, filter);
  };

  const filterProperties = {
    changeFilter,
    filterData: {...filter},
  };

  const [currentTab, setCurrentTab] = useState(0);

  const showDeleteButton = (user: UserActions.UserType) =>
    user.linesOfBusiness.some(lob => {
      switch (lob.id) {
        case LineOfBusiness.Agent:
          if (hasAccessToScopeByApplication(scopes, Scope.DeleteUser, Application.Agent)) {
            return true;
          }
          break;
        case LineOfBusiness.EnrollmentTracking:
          if (hasAccessToScopeByApplication(scopes, Scope.DeleteUser, Application.EnrollTrack)) {
            return true;
          }
          break;
        case LineOfBusiness.Beneficiary:
          if (hasAccessToScopeByApplication(scopes, Scope.DeleteUser, Application.Beneficiary)) {
            return true;
          }
          break;
        case LineOfBusiness.BOSTools:
          if (hasAccessToScopeByApplication(scopes, Scope.DeleteUser, Application.BOSTools)) {
            return true;
          }
          break;
        default:
          if (scopes.find(scope => scope.toString() === Scope.DeleteUser + '|*')) {
            return true;
          }
      }

      return false;
    });

  const getActionsMenu = (user: UserActions.UserType, scopes: Scope[]) => {
    return [
      scopes.includes(Scope.ResetUserWithTemporaryPassword) &&
        user.exclusivityStatus.id === ExclusivityStatus.External && (
          <MenuItem onClick={() => setUserToSendTemporaryPassword({userId: user.id, username: user.userName})}>
            Send Temporary Password
          </MenuItem>
        ),
      showDeleteButton(user) && <MenuItem onClick={() => setDeleteConfirmation(O.some({user}))}>Delete</MenuItem>,
    ].filter(Boolean);
  };

  const UsersAppBar = (
    <div className="commissions__tabs">
      <AppBar position="static">
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => {
            setCurrentTab(newValue);
          }}
        >
          <Tab key="users-tab" label="Users" />
          {/* <Tab key="pending-registrations-tab" label="Pending Registrations" /> */}
        </Tabs>
      </AppBar>
    </div>
  );

  const Users = (
    <>
      <PagedView
        pagedViewKey="userAdministration"
        loadMore={page => fetchUsers(page, filter)}
        data={users}
        actions={<AddUsersMenu {...props} />}
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="Line of Business"
              filterProperties={withSelectFilterProperties(filterProperties, 'linesOfBusiness', LinesOfBusiness)}
              default
            >
              {user => user.linesOfBusiness.map(lineOfBusiness => lineOfBusiness.name!).join(', ')}
            </Column>
            <Column
              {...columnProps}
              title="First Name"
              filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
              default
            >
              {user => user.firstName}
            </Column>
            <Column
              {...columnProps}
              title="Last Name"
              filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
              default
            >
              {user => user.lastName}
            </Column>
            <Column
              {...columnProps}
              title="Username"
              filterProperties={withStringFilterProperties(filterProperties, 'username')}
              default
            >
              {user => user.userName}
            </Column>
            <Column
              {...columnProps}
              title="Email"
              filterProperties={withStringFilterProperties(filterProperties, 'email')}
              default
            >
              {user => user.email}
            </Column>
            <Column {...columnProps} title="" isActions default>
              {user => (
                <>
                  {getActionsMenu(user, props.scopes).length > 0 ? (
                    <ActionsMenu>{getActionsMenu(user, props.scopes)}</ActionsMenu>
                  ) : null}
                  <Button color="primary" onClick={() => props.history.push(UserRouteConstants.EDIT_USER(user.id))}>
                    Edit
                  </Button>
                </>
              )}
            </Column>
          </>
        )}
      </PagedView>
    </>
  );

  return (
    <>
      {O.isSome(deleteConfirmation) && (
        <DeleteUserConfirmationModal open user={deleteConfirmation.value.user} onClose={hideDeleteConfirmation} />
      )}
      {userToSendTemporaryPassword && (
        <ResetUserPasswordConfirmationModal
          userId={userToSendTemporaryPassword.userId}
          username={userToSendTemporaryPassword.username}
          open
          onClose={() => setUserToSendTemporaryPassword(null)}
        />
      )}
      {UsersAppBar}
      {/* {currentTab === 0 ? Users : <PendingUserRegistration />} */}
      {Users}
    </>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  users: state.user.userAdministration.users,
  userClaims: state.user.profile.userClaims,
  filter: state.user.userAdministration.filter,

  scopes: selectUserScopes(state),
});

export default connect(mapStateToProps, {
  ...UserProfileActions,
  ...UserActions,
  ...ModalActions,
})(withWidth()(withRouter(UsersView)));
